import {lazy, Suspense, useCallback} from 'react';
import clsx from 'clsx';
import {useBlocker, useNavigation, useLocation} from 'react-router';
import ConfirmDialog from '@/components/confirm-dialog';
import ContainerCard from '@/components/container-card';
import {
  useDocumentLocale,
  usePrefilledSourceInvestorAccount,
  useRefreshPrefilledTransaction,
} from '@/components/document/hooks';
import Spinner from '@/components/spinner';
import {SUMMARY_SECTION} from '@/constants/sections';
import environment from '@/environment';
import {useFormPageViewEvent} from '@/hooks/analytics/use-form-page-view-event';
import {useDocumentContext} from './context/use-document-context';
import {useInspector} from './hooks/use-inspector';
import {useNavigationConfirmationActions} from './hooks/use-navigation-confirmation-actions';
import {useShouldBlockNavigation} from './hooks/use-should-block-navigation';
import {Section} from './section';
import {DocumentSummary} from './summary/document-summary';
import DocumentToolbar from './toolbar';

const DevInspector = lazy(() => import('./inspector/dev-inspector'));

export const DocumentContent = () => {
  const {t} = useDocumentLocale();
  const {state} = useNavigation();
  const location = useLocation();
  const devInspectorPosition = useInspector(state => state.position);

  const {
    document,
    isSaving,
    hasSavingError,
    hasUnsavedChanges,
    saveChanges,
    currentSectionId: currentSection,
    sectionRemountKey,
    submitSection,
    submitForSignature,
    resubmitForSignature,
  } = useDocumentContext();

  const blocker = useBlocker(useShouldBlockNavigation(hasUnsavedChanges));

  const isNavigationBlocked = blocker.state === 'blocked';
  const isNextVisible = currentSection !== SUMMARY_SECTION || document.status === 'incomplete';
  const isResendingDocument = location.state?.isResendingDocument;
  const isSectionSelectorVisible = !isResendingDocument;

  const showSubmitLabel = currentSection === SUMMARY_SECTION && document.status === 'incomplete';

  const confirmActions = useNavigationConfirmationActions({blocker, saveChanges});

  const onNext = useCallback(async () => {
    if (isResendingDocument) {
      await resubmitForSignature();
    } else if (currentSection !== SUMMARY_SECTION) {
      submitSection();
    } else if (document.status === 'incomplete') {
      submitForSignature();
    }
  }, [isResendingDocument, currentSection, document.status, resubmitForSignature, submitSection, submitForSignature]);

  useFormPageViewEvent();
  useRefreshPrefilledTransaction();
  usePrefilledSourceInvestorAccount();

  return (
    <div
      className={clsx(
        'flex max-w-full grow gap-4',
        devInspectorPosition === 'side' && 'flex-row items-start',
        devInspectorPosition === 'bottom' && 'flex-col',
      )}
    >
      {state === 'loading' ? (
        <div className="flex min-h-96 w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <ContainerCard>
            <DocumentToolbar
              isSectionSelectorVisible={isSectionSelectorVisible}
              isNextVisible={isNextVisible}
              showSubmitLabel={showSubmitLabel}
              isSaving={isSaving}
              hasSavingError={hasSavingError}
              onNext={onNext}
            />

            <ContainerCard.Content>
              {currentSection === SUMMARY_SECTION ? <DocumentSummary /> : <Section key={sectionRemountKey} />}
            </ContainerCard.Content>
          </ContainerCard>

          <ConfirmDialog
            isVisible={isNavigationBlocked}
            title={t('components.document.navigation-confirm.title')}
            text={t('components.document.navigation-confirm.text')}
            actions={confirmActions}
          />

          {environment.DEBUG && (
            <Suspense fallback={<Spinner />}>
              <DevInspector />
            </Suspense>
          )}
        </>
      )}
    </div>
  );
};
